import React, { createContext, useMemo } from 'react';

import {
  PlaceEacOrderSubmitRequest,
  PlaceEacOrderSubmitResponse,
  CreateEacEntitlementSubmitRequest,
  CreateEacEntitlementSubmitResponse,
} from '@amzn/coral_com-amazon-sssapinterface-model-operations-credits-submit';
import {
  GetEacProductViewRequest,
  GetEacProductViewResponse,
  GetEacProductPreviewRequest,
  GetEacProductPreviewResponse,
  ListEacOrdersViewRequest,
  ListEacOrdersViewResponse,
  ListEacEntitlementsViewRequest,
  ListEacEntitlementsViewResponse,
  GetEacEntitlementViewRequest,
  GetEacEntitlementViewResponse,
} from '@amzn/coral_com-amazon-sssapinterface-model-operations-credits-view';

import { sssApiClientSingleton as sssAPIClient } from 'src/clients/sss-api/sss-api-client';

const CLIENT_NOT_DEFINED_MESSAGE = 'SSSAPI client is not defined';

const getEacProduct = async (asin: string): Promise<GetEacProductViewResponse> => {
  const request = GetEacProductViewRequest.clone({ asin });

  if (!sssAPIClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return sssAPIClient.getEacProductView(request).toPromise();
};

const listEacOrders = async (): Promise<ListEacOrdersViewResponse> => {
  const request = ListEacOrdersViewRequest.clone({});

  if (!sssAPIClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return sssAPIClient.listEacOrdersView(request).toPromise();
};

const listEacReservations = async (): Promise<ListEacEntitlementsViewResponse> => {
  const request = ListEacEntitlementsViewRequest.clone({
    shouldFilterOutOrders: true,
  });

  if (!sssAPIClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return sssAPIClient.listEacEntitlementsView(request).toPromise();
};

const placeEacOrder = async (entitlementId: string): Promise<PlaceEacOrderSubmitResponse> => {
  const request = PlaceEacOrderSubmitRequest.clone({
    entitlementId,
  });

  if (!sssAPIClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return sssAPIClient.placeEacOrderSubmit(request).toPromise();
};

const createEacEntitlement = async (
  request: CreateEacEntitlementSubmitRequest
): Promise<CreateEacEntitlementSubmitResponse> => {
  if (!sssAPIClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return sssAPIClient.createEacEntitlementSubmit(request).toPromise();
};

const getEacEntitlement = async (entitlementId: string): Promise<GetEacEntitlementViewResponse> => {
  const request = GetEacEntitlementViewRequest.clone({
    entitlementId,
  });

  if (!sssAPIClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return sssAPIClient.getEacEntitlementView(request).toPromise();
};

const getEacProductPreview = async (asin: string): Promise<GetEacProductPreviewResponse> => {
  const request = GetEacProductPreviewRequest.clone({ asin });

  if (!sssAPIClient) {
    throw new Error(CLIENT_NOT_DEFINED_MESSAGE);
  }

  return sssAPIClient.getEacProductPreview(request).toPromise();
};

export interface SssApiCreditsContext {
  getEacProduct: (eacId: string) => Promise<GetEacProductViewResponse>;
  getEacProductPreview: (asin: string) => Promise<GetEacProductPreviewResponse>;
  listEacOrders: () => Promise<ListEacOrdersViewResponse>;
  listEacReservations: () => Promise<ListEacEntitlementsViewResponse>;
  placeEacOrder: (entitlementId: string) => Promise<PlaceEacOrderSubmitResponse>;
  createEacEntitlement: (request: CreateEacEntitlementSubmitRequest) => Promise<CreateEacEntitlementSubmitResponse>;
  getEacEntitlement: (entitlementId: string) => Promise<GetEacEntitlementViewResponse>;
}

export const SssApiCreditsClientContext = createContext<SssApiCreditsContext | null>(null);

export const SssApiCreditsClientProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const contextValue = useMemo<SssApiCreditsContext>(
    () => ({
      getEacProduct,
      getEacProductPreview,
      listEacOrders,
      listEacReservations,
      placeEacOrder,
      createEacEntitlement,
      getEacEntitlement,
    }),
    []
  );
  return <SssApiCreditsClientContext.Provider value={contextValue}>{children}</SssApiCreditsClientContext.Provider>;
};
